<script setup>
import { computed, ref, watch } from 'vue';
import { useDocumentVisibility, useIntervalFn, useNow } from '@vueuse/core';
import { useI18n } from 'vue-i18n';
import { useAbortableRequest } from 'dashboard/composables/useAbortableRequest';
import GowaAPI from 'dashboard/api/gowa';

const props = defineProps({
  accountId: { type: Number, required: true },
  conversationId: { type: Number, required: true },
});

const POLL_INTERVAL = 10000;
const FRESHNESS = 60000;
const { t, locale } = useI18n();
const visibility = useDocumentVisibility();
const now = useNow({ interval: 1000 });
const presence = ref({ state: 'unknown' });
const { run, abort, isPending } = useAbortableRequest();

const refresh = async () => {
  if (visibility.value !== 'visible' || isPending.value) return;
  const conversationId = props.conversationId;
  try {
    const response = await run(signal =>
      GowaAPI.contactPresence(props.accountId, conversationId, signal)
    );
    if (response && conversationId === props.conversationId) {
      presence.value = response.data;
    }
  } catch {
    presence.value = { state: 'unavailable' };
  }
};

useIntervalFn(refresh, POLL_INTERVAL);
watch(
  () => [props.accountId, props.conversationId, visibility.value],
  () => {
    abort();
    presence.value = { state: 'unknown' };
    refresh();
  },
  { immediate: true }
);

const state = computed(() => {
  if (['self', 'unsupported'].includes(presence.value.state)) {
    return presence.value.state;
  }
  const observed = Date.parse(presence.value.observed_at);
  return now.value.getTime() - observed < FRESHNESS
    ? presence.value.state
    : 'unknown';
});

const label = computed(() => {
  if (state.value === 'self') return t('GOWA_PRESENCE.SELF');
  if (state.value === 'online') return t('GOWA_PRESENCE.ONLINE');
  if (state.value === 'offline' && presence.value.last_seen) {
    const date = new Date(presence.value.last_seen);
    return t('GOWA_PRESENCE.LAST_SEEN', {
      time: new Intl.DateTimeFormat(locale.value.replace('_', '-'), {
        dateStyle: 'short',
        timeStyle: 'short',
      }).format(date),
    });
  }
  if (state.value === 'unsupported') return '';
  return t('GOWA_PRESENCE.UNAVAILABLE');
});
</script>

<template>
  <span
    v-if="label"
    v-tooltip="$t('GOWA_PRESENCE.TOOLTIP')"
    class="inline-flex items-center gap-1 truncate text-xs"
    :class="state === 'online' ? 'text-n-teal-11' : 'text-n-slate-11'"
    data-testid="gowa-contact-presence"
    role="status"
  >
    <span>•</span>
    <span>{{ label }}</span>
  </span>
</template>
